import type {
   ChartAnalysisSummary,
   ChartDataPoint,
   FibonacciZone,
   MarketZone,
   TimeRange,
   TrendChannelData
} from "@/types";
import { getValueUnitAndTerm } from "@/app/lib/agents/utils";

export function aggregateToWeeklyCandles(dailyData: ChartDataPoint[]): ChartDataPoint[] {
   if (dailyData.length < 7) return dailyData;

   const weeklyCandles: ChartDataPoint[] = [];

   for (let i = 0; i < dailyData.length; i += 7) {
      const weekData = dailyData.slice(i, Math.min(i + 7, dailyData.length));
      if (weekData.length === 0) break;

      const weekCandle: ChartDataPoint = {
         timestamp: weekData[0].timestamp,
         unixTime: weekData[0].unixTime,
         open: weekData[0].open,
         high: Math.max(...weekData.map((d) => d.high)),
         low: Math.min(...weekData.map((d) => d.low)),
         close: weekData[weekData.length - 1].close,
         volume: weekData.reduce((sum, d) => sum + (d.volume || 0), 0)
      };

      weeklyCandles.push(weekCandle);
   }

   return weeklyCandles;
}

const SWING_WINDOW_RATIOS = [8, 12, 16, 24, 32, 48];
const MIN_SWING_SPAN_RATIO = 0.4;
const MAX_ANCHOR_AGE_RATIO = 0.25;

type SwingPoint = { x: number; y: number };

function findSwingLows(data: ChartDataPoint[], window: number): SwingPoint[] {
   const n = data.length;
   const lows: SwingPoint[] = [];

   for (let i = 0; i < n; i++) {
      // Clamping instead of skipping the edges is load-bearing: the newest trough is
      // never confirmed on both sides, and it is the one the support line anchors on.
      const from = Math.max(0, i - window);
      const to = Math.min(n - 1, i + window);

      let isSwingLow = true;
      for (let j = from; j <= to; j++) {
         if (data[j].low < data[i].low) isSwingLow = false;
      }
      if (isSwingLow) lows.push({ x: i - (n - 1), y: data[i].low });
   }

   return lows;
}

export function calculateTrendChannel(data: ChartDataPoint[]): TrendChannelData | null {
   if (!data || data.length < 3) return null;

   const n = data.length;

   // Widest window first, so the support line rides cycle-level troughs rather than minor
   // dips. It narrows only until those troughs both reach across the chart and include a
   // recent one — a stale anchor gets projected forward from an old regime, which is how a
   // fallen asset ends up with rails climbing off the top of the chart.
   const reachesAcross = (points: SwingPoint[]) =>
      points[points.length - 1].x - points[0].x >= MIN_SWING_SPAN_RATIO * (n - 1);
   const anchorIsRecent = (points: SwingPoint[]) =>
      points[points.length - 1].x >= -MAX_ANCHOR_AGE_RATIO * (n - 1);

   let swingLows: SwingPoint[] | null = null;
   for (const ratio of SWING_WINDOW_RATIOS) {
      const lows = findSwingLows(data, Math.max(1, Math.round(n / ratio)));
      if (lows.length < 2) continue;

      swingLows = lows;
      if (reachesAcross(lows) && anchorIsRecent(lows)) break;
   }

   if (!swingLows) {
      swingLows = data.map((d, i) => ({ x: i - (n - 1), y: d.low }));
   }

   // Support line through the two most recent troughs. Picking the extreme slope rather
   // than the immediately preceding trough is what keeps every other low above the line;
   // the nearest one alone lets older troughs cut through it.
   const anchor = swingLows[swingLows.length - 1];
   let slope: number | null = null;
   for (let i = 0; i < swingLows.length - 1; i++) {
      const run = anchor.x - swingLows[i].x;
      if (run <= 0) continue;

      const candidate = (anchor.y - swingLows[i].y) / run;
      if (slope === null || candidate > slope) slope = candidate;
   }
   if (slope === null) return null;

   const bottomIntercept = anchor.y - slope * anchor.x;
   // Resistance is a parallel copy lifted onto the highest high, never fitted separately —
   // two independently anchored rails converge into a wedge instead of a channel.
   const topIntercept = Math.max(...data.map((d, i) => d.high - slope * (i - (n - 1))));

   const height = topIntercept - bottomIntercept;
   if (!(height > 0)) return null;

   const topLine: { time: number; value: number }[] = [];
   const bottomLine: { time: number; value: number }[] = [];
   const mid1Line: { time: number; value: number }[] = [];
   const mid2Line: { time: number; value: number }[] = [];

   for (let i = 0; i < n; i++) {
      const bottom = slope * (i - (n - 1)) + bottomIntercept;
      const time = data[i].unixTime;

      bottomLine.push({ time, value: bottom });
      mid2Line.push({ time, value: bottom + height / 3 });
      mid1Line.push({ time, value: bottom + (height * 2) / 3 });
      topLine.push({ time, value: bottom + height });
   }

   const lastIdx = n - 1;
   const currentPrice = data[lastIdx].close;

   let currentZone = "Holding Zone (Middle)";
   if (currentPrice > mid1Line[lastIdx].value) currentZone = "Selling Zone (High)";
   else if (currentPrice < mid2Line[lastIdx].value) currentZone = "Buying Zone (Low)";

   return { topLine, bottomLine, mid1Line, mid2Line, slope, currentZone };
}

export function formatChartSummaryForLLM(summary: ChartAnalysisSummary): string {
   const { valueTerm, formatValue } = getValueUnitAndTerm(summary.symbol, summary.name);

   return `📊 Analysis ${summary.symbol} scores (${summary.name}) - ${
      summary.periodDescription
   }

📈 Peak : ${valueTerm} ${formatValue(summary.peak.price)} (${
      summary.peak.date
   }, ${summary.peak.daysAgo} days ago)
📉 Bottom: ${valueTerm} ${formatValue(summary.bottom.price)} (${
      summary.bottom.date
   }, ${summary.bottom.daysAgo} days ago)

🎯 Current ${valueTerm} Position:
- ${valueTerm}: ${formatValue(summary.currentPrice)}
- ${summary.currentZoneDescription}
- ${valueTerm} from peak: ${summary.priceChangeFromPeak}%
- ${valueTerm} from bottom: +${summary.priceChangeFromBottom}%

📐 3 Trend Channel Zones for ${valueTerm} (parallel channel anchored on recent swing highs/lows):
1. ${summary.fibonacciZones[0].name}: ${formatValue(
      summary.fibonacciZones[0].priceRange.from
   )} - ${formatValue(summary.fibonacciZones[0].priceRange.to)} (${
      summary.fibonacciZones[0].rangePercent
   })
2. ${summary.fibonacciZones[1].name}: ${formatValue(
      summary.fibonacciZones[1].priceRange.from
   )} - ${formatValue(summary.fibonacciZones[1].priceRange.to)} (${
      summary.fibonacciZones[1].rangePercent
   })
3. ${summary.fibonacciZones[2].name}: ${valueTerm} ${formatValue(
      summary.fibonacciZones[2].priceRange.from
   )} - ${formatValue(summary.fibonacciZones[2].priceRange.to)} (${
      summary.fibonacciZones[2].rangePercent
   })`;
}

export function analyzeChartDataForLLM(
   dataPoints: ChartDataPoint[],
   symbol: string,
   name: string,
   timeRange: TimeRange
): ChartAnalysisSummary {
   if (!dataPoints || dataPoints.length === 0) {
      throw new Error("No data points to analyze");
   }

   const weeklyData = aggregateToWeeklyCandles(dataPoints);
   const analysisData =
      dataPoints.length >= 7 && weeklyData.length >= 1 ? weeklyData : dataPoints;

   const trendChannel = calculateTrendChannel(analysisData);

   let peakIndex = 0;
   let bottomIndex = 0;
   let peakPrice = analysisData[0].high;
   let bottomPrice = analysisData[0].low;

   for (let i = 0; i < analysisData.length; i++) {
      if (analysisData[i].high > peakPrice) {
         peakPrice = analysisData[i].high;
         peakIndex = i;
      }
      if (analysisData[i].low < bottomPrice) {
         bottomPrice = analysisData[i].low;
         bottomIndex = i;
      }
   }

   const currentData = analysisData[analysisData.length - 1];
   const currentPrice = currentData.close;
   const currentDate = currentData.timestamp;

   const { valueTerm, formatValue } = getValueUnitAndTerm(symbol, name);

   let currentZone: MarketZone;
   let currentZoneDescription: string;
   let fibonacciZones: FibonacciZone[];

   // Use Trend Channel if available, otherwise fallback to old method
   if (trendChannel && trendChannel.topLine.length > 0) {
      const lastIdx = trendChannel.topLine.length - 1;
      const channelTop = trendChannel.topLine[lastIdx].value;
      const channelBottom = trendChannel.bottomLine[lastIdx].value;
      const limit33 = trendChannel.mid2Line[lastIdx].value;
      const limit66 = trendChannel.mid1Line[lastIdx].value;

      // Define 3 zones based on channel
      fibonacciZones = [
         {
            name: "Buying Zone",
            zone: "BOTTOM",
            rangePercent: "0% - 33% (from channel bottom)",
            priceRange: {
               from: channelBottom,
               to: limit33
            }
         },
         {
            name: "Holding Zone",
            zone: "MIDDLE",
            rangePercent: "33% - 66% (from channel bottom)",
            priceRange: {
               from: limit33,
               to: limit66
            }
         },
         {
            name: "Selling Zone",
            zone: "TOP",
            rangePercent: "66% - 100% (from channel bottom)",
            priceRange: {
               from: limit66,
               to: channelTop
            }
         }
      ];

      // Determine current zone
      if (currentPrice > limit66) {
         currentZone = "TOP";
         currentZoneDescription = `${valueTerm} is in the SELLING ZONE (High) - above 66% of channel range. Current: ${formatValue(
            currentPrice
         )}, Zone: ${formatValue(limit66)} - ${formatValue(channelTop)}`;
      } else if (currentPrice < limit33) {
         currentZone = "BOTTOM";
         currentZoneDescription = `${valueTerm} is in the BUYING ZONE (Low) - below 33% of channel range. Current: ${formatValue(
            currentPrice
         )}, Zone: ${formatValue(channelBottom)} - ${formatValue(limit33)}`;
      } else {
         currentZone = "MIDDLE";
         currentZoneDescription = `${valueTerm} is in the HOLDING ZONE (Middle) - between 33% and 66% of channel range. Current: ${formatValue(
            currentPrice
         )}, Zone: ${formatValue(limit33)} - ${formatValue(limit66)}`;
      }
   } else {
      // Fallback to old Fibonacci method if trend channel calculation fails
      const fullPriceRange = peakPrice - bottomPrice;
      const zoneRange = fullPriceRange * 0.75;
      const zoneBottom = peakPrice - zoneRange;
      const zoneSize = zoneRange / 3;

      fibonacciZones = [
         {
            name: "Top Zone (Overbought)",
            zone: "TOP",
            rangePercent: "0% - 25% (from peak)",
            priceRange: {
               from: peakPrice - zoneSize,
               to: peakPrice
            }
         },
         {
            name: "Middle Zone (Neutral)",
            zone: "MIDDLE",
            rangePercent: "25% - 50% (from peak)",
            priceRange: {
               from: peakPrice - zoneSize * 2,
               to: peakPrice - zoneSize
            }
         },
         {
            name: "Bottom Zone (Oversold)",
            zone: "BOTTOM",
            rangePercent: "50% - 75% (from peak)",
            priceRange: {
               from: zoneBottom,
               to: peakPrice - zoneSize * 2
            }
         }
      ];

      const distanceFromPeak = peakPrice - currentPrice;
      const percentFromPeak = zoneRange > 0 ? (distanceFromPeak / zoneRange) * 100 : 0;

      if (currentPrice >= peakPrice - zoneSize) {
         currentZone = "TOP";
         currentZoneDescription = `${valueTerm} is in the TOP ZONE (${percentFromPeak.toFixed(
            1
         )}% from peak), near overbought`;
      } else if (currentPrice >= peakPrice - zoneSize * 2) {
         currentZone = "MIDDLE";
         currentZoneDescription = `${valueTerm} is in the MIDDLE ZONE (${percentFromPeak.toFixed(
            1
         )}% from peak), neutral zone`;
      } else if (currentPrice >= zoneBottom) {
         currentZone = "BOTTOM";
         currentZoneDescription = `${valueTerm} is in the BOTTOM ZONE (${percentFromPeak.toFixed(
            1
         )}% from peak), near oversold`;
      } else {
         currentZone = "BOTTOM";
         currentZoneDescription = `${valueTerm} is below the ANALYSIS ZONE (${percentFromPeak.toFixed(
            1
         )}% from peak, below 75%), strong oversold`;
      }
   }

   const fullPriceRange = peakPrice - bottomPrice;
   const distanceFromPeak = peakPrice - currentPrice;
   const percentFromPeak =
      fullPriceRange > 0 ? (distanceFromPeak / fullPriceRange) * 100 : 0;

   const now = new Date();
   const peakDate = new Date(analysisData[peakIndex].timestamp);
   const bottomDate = new Date(analysisData[bottomIndex].timestamp);
   const peakDaysAgo = Math.floor(
      (now.getTime() - peakDate.getTime()) / (1000 * 60 * 60 * 24)
   );
   const bottomDaysAgo = Math.floor(
      (now.getTime() - bottomDate.getTime()) / (1000 * 60 * 60 * 24)
   );

   const timeRangeDescMap: Record<TimeRange, string> = {
      "7d": "7 days",
      "30d": "30 days (1 month)",
      "90d": "90 days (3 months)",
      "1y": "1 year",
      all: "all history"
   };

   return {
      symbol,
      name,
      timeRange,
      periodDescription: timeRangeDescMap[timeRange],
      currentPrice,
      currentDate,

      peak: {
         price: peakPrice,
         date: analysisData[peakIndex].timestamp,
         daysAgo: peakDaysAgo
      },
      bottom: {
         price: bottomPrice,
         date: analysisData[bottomIndex].timestamp,
         daysAgo: bottomDaysAgo
      },

      fibonacciZones,
      currentZone,
      currentZoneDescription,
      percentFromBottom: parseFloat(
         (fullPriceRange > 0
            ? ((currentPrice - bottomPrice) / fullPriceRange) * 100
            : 0
         ).toFixed(2)
      ),
      percentFromPeak: parseFloat(percentFromPeak.toFixed(2)),

      priceChangeFromPeak: parseFloat(
         (((currentPrice - peakPrice) / peakPrice) * 100).toFixed(2)
      ),
      priceChangeFromBottom: parseFloat(
         (((currentPrice - bottomPrice) / bottomPrice) * 100).toFixed(2)
      ),
      totalDataPoints: dataPoints.length
   };
}
